"use client";

import React from "react";
import Link from "next/link";
import { Button, type ButtonProps } from "@/components/ui/button";
import { MessageSquare, Clock, Zap } from "lucide-react";

interface RequestFixCTAProps extends Omit<ButtonProps, "asChild" | "size" | "variant"> {
  /** URL to navigate to for the request */
  href?: string;
  /** Button label variant */
  variant?: "urgent" | "book" | "whatsapp" | "default";
  /** Size of the button */
  buttonSize?: "sm" | "md" | "lg";
  /** Pro ID for direct routing */
  proId?: string;
  /** Service type for request pre-fill */
  serviceType?: string;
  /** Full width */
  fullWidth?: boolean;
}

const LABEL_MAP: Record<string, { label: string; icon: React.ReactNode; description?: string }> = {
  urgent: {
    label: "Request the Squad",
    icon: <Zap size={16} />,
    description: "Get help in minutes",
  },
  book: {
    label: "Book This Pro",
    icon: <Clock size={16} />,
    description: "Reserve a time",
  },
  whatsapp: {
    label: "Chat on WhatsApp",
    icon: <MessageSquare size={16} />,
    description: "Direct message",
  },
  default: {
    label: "Request a Fix",
    icon: <Clock size={16} />,
  },
};

export function RequestFixCTA({
  href,
  variant = "default",
  buttonSize = "md",
  proId,
  serviceType,
  fullWidth = false,
  ...buttonProps
}: RequestFixCTAProps) {
  const config = LABEL_MAP[variant] || LABEL_MAP.default;

  let finalHref = href || "/fix-it/request";

  // Build query params
  const params = new URLSearchParams();
  if (proId) params.append("proId", proId);
  if (serviceType) params.append("service", serviceType);
  const queryString = params.toString();
  if (queryString) {
    finalHref += `?${queryString}`;
  }

  const colorClass =
    variant === "urgent"
      ? "bg-red-600 hover:bg-red-700 text-white"
      : variant === "book"
        ? "bg-blue-600 hover:bg-blue-700 text-white"
        : variant === "whatsapp"
          ? "bg-green-600 hover:bg-green-700 text-white"
          : "bg-[#001489] hover:bg-[#000d64] text-white";

  const sizeClass =
    buttonSize === "sm"
      ? "px-3 py-1.5 text-sm"
      : buttonSize === "lg"
        ? "px-6 py-3 text-lg"
        : "px-4 py-2 text-base";

  return (
    <Button
      asChild
      className={`${colorClass} ${sizeClass} font-bold shadow-md transition flex items-center gap-2 ${fullWidth ? "w-full" : ""}`}
      {...buttonProps}
    >
      <Link href={finalHref}>
        {config.icon}
        {config.label}
      </Link>
    </Button>
  );
}
